import { useState } from "react";
import { motion } from "framer-motion";
import type { IntroBlock, ExplainBlock, ExampleBlock, FlashcardsBlock, SummaryBlock } from "@shared/lessonBlocks";
import { Button } from "@/components/ui/button";
import { Lightbulb, Brain, Sparkles, MapPin, AlertTriangle, ChevronRight, CheckCircle2, Award, Target } from "lucide-react";
import { RichText } from "../RichText";
import { Mascot } from "../Mascot";
import { LessonVisualView } from "../Visuals";

const CALLOUT_STYLE = {
  tip: { icon: Lightbulb, label: "Tip", cls: "border-sky-300 bg-sky-50 dark:bg-sky-900/25 text-sky-900 dark:text-sky-100" },
  remember: { icon: Brain, label: "Remember", cls: "border-violet-300 bg-violet-50 dark:bg-violet-900/25 text-violet-900 dark:text-violet-100" },
  funfact: { icon: Sparkles, label: "Fun fact", cls: "border-amber-300 bg-amber-50 dark:bg-amber-900/25 text-amber-900 dark:text-amber-100" },
  jamaica: { icon: MapPin, label: "Jamaica", cls: "border-emerald-300 bg-emerald-50 dark:bg-emerald-900/25 text-emerald-900 dark:text-emerald-100" },
  warning: { icon: AlertTriangle, label: "Watch out", cls: "border-rose-300 bg-rose-50 dark:bg-rose-900/25 text-rose-900 dark:text-rose-100" },
} as const;

export function IntroBlockView({ block }: { block: IntroBlock }) {
  return (
    <div className="space-y-5 text-center">
      {/* No fallback emoji: DESIGN_SYSTEM.md bans emoji in the interface, and lessons that set
          emoji: "" mean it. A book glyph on every intro would defeat that. */}
      {block.emoji && (
        <div className="text-7xl select-none animate-in zoom-in-50 duration-500">
          {block.emoji}
        </div>
      )}
      <h2 className="text-2xl md:text-3xl font-extrabold tracking-tight" data-testid="text-lesson-intro-title">{block.title}</h2>
      <Mascot mood="wave" message={block.text} className="text-left" />
      {block.goals && block.goals.length > 0 && (
        <div className="rounded-2xl bg-primary/5 border border-primary/20 p-4 text-left">
          <p className="font-bold flex items-center gap-2 mb-2"><Target className="w-4 h-4 text-primary" /> By the end you will be able to:</p>
          <ul className="space-y-1.5">
            {block.goals.map((g, i) => (
              <li key={i} className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-primary mt-1 flex-shrink-0" /><span>{g}</span></li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export function ExplainBlockView({ block }: { block: ExplainBlock }) {
  const callout = block.callout ? CALLOUT_STYLE[block.callout.kind] : null;
  const CalloutIcon = callout?.icon;
  return (
    <div className="space-y-4">
      <h2 className="text-xl md:text-2xl font-extrabold tracking-tight"><RichText text={block.heading} inline /></h2>
      <RichText text={block.body} className="text-base md:text-lg" />
      {block.imageUrl && (
        <img src={block.imageUrl} alt={block.imageAlt || ""} className="rounded-2xl max-h-72 mx-auto object-contain" loading="lazy" />
      )}
      {block.visual && <div className="pt-1"><LessonVisualView visual={block.visual} /></div>}
      {block.callout && callout && CalloutIcon && (
        <div className={`rounded-2xl border-2 p-4 flex items-start gap-3 ${callout.cls}`}>
          <CalloutIcon className="w-5 h-5 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-xs uppercase tracking-wide font-bold opacity-70 mb-0.5">{callout.label}</p>
            <RichText text={block.callout.text} />
          </div>
        </div>
      )}
    </div>
  );
}

export function ExampleBlockView({ block }: { block: ExampleBlock }) {
  const [revealed, setRevealed] = useState(1);
  const allShown = revealed >= block.steps.length;
  return (
    <div className="space-y-4">
      <h2 className="text-xl md:text-2xl font-extrabold tracking-tight"><RichText text={block.heading} inline /></h2>
      <div className="rounded-2xl bg-muted/50 border border-border p-4">
        <p className="text-xs uppercase tracking-wide font-bold text-muted-foreground mb-1">Problem</p>
        <RichText text={block.problem} className="text-lg font-medium" />
      </div>
      <ol className="space-y-2">
          {block.steps.slice(0, revealed).map((step, i) => (
            <li key={i} className="flex items-start gap-3 rounded-xl border border-border bg-card p-3 animate-in fade-in slide-in-from-left-3 duration-300">
              <span className="w-7 h-7 rounded-full bg-primary text-primary-foreground text-sm font-bold flex items-center justify-center flex-shrink-0">{i + 1}</span>
              <div className="flex-1">
                <RichText text={step.text} />
                {step.note && <p className="text-xs text-muted-foreground mt-1">{step.note}</p>}
              </div>
            </li>
          ))}
      </ol>
      {!allShown ? (
        <Button variant="outline" className="rounded-xl" onClick={() => setRevealed(r => r + 1)} data-testid="button-example-next-step">
          Show next step <ChevronRight className="w-4 h-4 ml-1" />
        </Button>
      ) : block.answer ? (
        <div className="rounded-2xl border-2 border-emerald-300 bg-emerald-50 dark:bg-emerald-900/25 p-4 animate-in fade-in slide-in-from-bottom-1 duration-300">
          <p className="text-xs uppercase tracking-wide font-bold text-emerald-700 dark:text-emerald-300 mb-1">Answer</p>
          <RichText text={block.answer} className="text-lg font-semibold" />
        </div>
      ) : null}
    </div>
  );
}

export function FlashcardsBlockView({ block }: { block: FlashcardsBlock }) {
  const [flipped, setFlipped] = useState<Set<number>>(new Set());
  const toggle = (i: number) => setFlipped(prev => { const n = new Set(prev); n.has(i) ? n.delete(i) : n.add(i); return n; });
  return (
    <div className="space-y-4">
      <h2 className="text-xl md:text-2xl font-extrabold tracking-tight"><RichText text={block.heading || "Word cards"} inline /></h2>
      <p className="text-sm text-muted-foreground">Tap a card to flip it. Flip them all before you continue.</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {block.cards.map((card, i) => {
          const isFlipped = flipped.has(i);
          return (
            <button
              key={i}
              type="button"
              onClick={() => toggle(i)}
              className="relative h-36 rounded-2xl [perspective:1000px] text-left"
              data-testid={`card-flash-${i}`}
              aria-pressed={isFlipped}
            >
              <motion.div
                animate={{ rotateY: isFlipped ? 180 : 0 }}
                transition={{ duration: 0.45 }}
                className="absolute inset-0 [transform-style:preserve-3d]"
              >
                <div className="absolute inset-0 rounded-2xl border-2 border-primary/30 bg-gradient-to-br from-primary/10 to-chart-2/10 p-4 flex flex-col items-center justify-center gap-1 [backface-visibility:hidden]">
                  {card.emoji && <span className="text-3xl">{card.emoji}</span>}
                  <RichText text={card.front} inline className="text-lg font-bold text-center" />
                  <span className="text-[11px] uppercase tracking-wide text-muted-foreground">tap to flip</span>
                </div>
                <div className="absolute inset-0 rounded-2xl border-2 border-emerald-300 bg-emerald-50 dark:bg-emerald-900/30 p-4 flex items-center justify-center text-center [backface-visibility:hidden] [transform:rotateY(180deg)]">
                  <RichText text={card.back} inline className="text-sm md:text-base font-medium" />
                </div>
              </motion.div>
            </button>
          );
        })}
      </div>
      <p className="text-xs text-muted-foreground">{flipped.size}/{block.cards.length} flipped</p>
    </div>
  );
}

export function SummaryBlockView({ block }: { block: SummaryBlock }) {
  return (
    <div className="space-y-4">
      <h2 className="text-xl md:text-2xl font-extrabold tracking-tight"><RichText text={block.heading || "What you learned"} inline /></h2>
      <ul className="space-y-2">
        {block.points.map((p, i) => (
          <li key={i} className="flex items-start gap-3 rounded-xl border border-border bg-card p-3 animate-in fade-in slide-in-from-left-2 duration-300 fill-mode-both" style={{ animationDelay: `${i * 80}ms` }}>
            <CheckCircle2 className="w-5 h-5 text-emerald-500 mt-0.5 flex-shrink-0" />
            <RichText text={p} />
          </li>
        ))}
      </ul>
      {block.badgeText && (
        <div className="rounded-2xl border-2 border-amber-300 bg-amber-50 dark:bg-amber-900/25 p-4 flex items-center gap-3">
          <Award className="w-8 h-8 text-amber-500" />
          <div>
            <p className="text-xs uppercase tracking-wide font-bold text-amber-700 dark:text-amber-300">Finish to earn the title</p>
            <p className="text-lg font-extrabold">{block.badgeText}</p>
          </div>
        </div>
      )}
    </div>
  );
}
